import type {
  HierarchyNode,
  HierarchyPointNode,
  HierarchyPointLink,
  PortfolioDiversification,
} from '../types';

import type { Theme } from '@simplywallst/ui-core';

export const TRUNK_WIDTH = 100;
export const DESKTOP_TRUNK_DEPTH = 80;
export const MOBILE_TRUNK_DEPTH = 40;

export const getBranchWidth = (weight: number) =>
  Math.max(1, TRUNK_WIDTH * weight);

const getTotalBranchWeight = (nodes: HierarchyPointNode[] = []) => {
  return nodes.reduce((a, b) => a + b.data.weight, 0);
};

export const getTotalBranchWidth = (nodes: HierarchyPointNode[] = []) => {
  return Math.max(1, Math.round(TRUNK_WIDTH * getTotalBranchWeight(nodes)));
};

export const getCurrentBranchWidth = (link: HierarchyPointLink) => {
  const { source, target } = link;
  let currentBranchWidth = 0;
  const children = source.children ?? [];
  for (let i = 0; i < children.length; i++) {
    currentBranchWidth = currentBranchWidth + children[i].data.weight;
    if (children[i].data.name === target.data.name) break;
  }
  return TRUNK_WIDTH * currentBranchWidth;
};

export const getSource = (
  link: HierarchyPointLink,
  trunkDepth = DESKTOP_TRUNK_DEPTH
): HierarchyPointNode => {
  const { source, target } = link;

  const totalBranchWidth = getTotalBranchWidth(link.source.children);
  const currentBranchWidth = getCurrentBranchWidth(link);

  const zero = currentBranchWidth + source.x - totalBranchWidth / 2;
  const width = TRUNK_WIDTH * target.data.weight;

  /**
   * In the chart SVG, branches are "strokes"
   * Therefore, we must offset their postion by half against the currentBranchWidth to align correctly
   *
           |‾‾‾‾‾|‾‾‾‾ zero
           |     |
     x ----       ---- x
           |     |
           |_____|
   */
  const x = zero - width / 2;

  // Apply extra node depth for the tree root source node
  if (source.depth === 0) {
    return {
      ...source,
      x,
      y: source.y + trunkDepth,
    };
  }

  return {
    ...source,
    x,
    y: source.y,
  };
};

export const collapseChildren = (data: PortfolioDiversification) => {
  const children = data?.children ?? [];
  return {
    ...data,
    children: children.reduce<PortfolioDiversification[]>((a, b) => {
      const children = b?.children ?? [];
      return [...a, ...children];
    }, []),
  };
};

type Position = {
  x: number;
  y: number;
  depth: number;
};

export const getLabelDepths = (
  node: HierarchyPointNode,
  result: Position[] = []
): Position[] => {
  if (node.children && node.children.length > 0) {
    return getLabelDepths(node.children[0], [
      ...result,
      {
        x: node.children[0].x,
        y: node.children[0].y,
        depth: node.children[0].depth,
      },
    ]);
  } else {
    return result;
  }
};

export const sortByWeight = (a: HierarchyNode, b: HierarchyNode) =>
  a.data.weight < b.data.weight ? -1 : 1;

export const getPaddingTop = (holdingsCount: number, theme: Theme) => {
  if (holdingsCount === 1) return theme.x10;

  if (holdingsCount === 2) return theme.x7;

  return theme.x5;
};

export const getVerticalNodeSpacing = (holdingsCount: number, theme: Theme) => {
  if (holdingsCount <= 5) return theme.x6;

  if (holdingsCount > 5 && holdingsCount <= 10) return theme.x4;

  return 26;
};
